import {useMemo} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';

import {ICategory} from 'coreStore/data/categories/types';
import {useCommerceAPI} from 'commerceAPI/contexts';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {useRequestClass} from 'coreHooks/index';
import useLanguageCode from 'coreHooks/locales/useLanguageCode';

import useIsCategoryTreeLoaded from './useIsCategoryTreeLoaded';

import {getCategoriesQueryKey, fetchCategoryTree, createCategoryResult, ICategoryResult} from 'coreUtils/categories';

/**
 * Hook to retrieve a category and its utility functions by id.
 * Subscribes only to the specific category via React Query's select,
 * so the component re-renders only when its category changes.
 *
 * If the category is not in cache and the root tree is loaded,
 * it will attempt a single fetch. Categories marked as _notFound
 * will not be retried (staleTime: Infinity).
 */
const useCategory = (id: string | null | undefined): ICategoryResult | null => {
	const {isCORSRequest} = useRequestClass();
	const {managerRepository} = useGeneralProvider();
	const localesMgr = managerRepository.getLocalesMgr();
	const queryClient = useQueryClient();
	const commerceApi = useCommerceAPI();
	const languageCode = useLanguageCode();

	const {data: category = null} = useQuery<Record<string, ICategory>, Error, ICategory | null>({
		queryKey: getCategoriesQueryKey(languageCode),
		queryFn: () => ({} as Record<string, ICategory>),
		enabled: false,
		staleTime: Infinity,
		select: (categories) => {
			if (!id) {
				return null;
			}

			const cat = categories?.[id];

			if (!cat || cat._notFound) {
				return null;
			}

			return cat;
		},
	});

	const rootLoaded = useIsCategoryTreeLoaded(languageCode);

	useQuery({
		queryKey: ['category-fetch', id, languageCode],
		queryFn: () => fetchCategoryTree(id!, commerceApi, queryClient, languageCode),
		enabled: !!id && !category && rootLoaded,
		staleTime: Infinity,
		retry: false,
	});

	return useMemo(
		() => {
			if (!id) {
				return null;
			}

			return createCategoryResult(category, {localesMgr, isCORSRequest, queryClient, languageCode});
		},
		[id, category, isCORSRequest, queryClient, languageCode]
	);
};

export default useCategory;
